import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Empty, Tooltip, Typography } from 'antd'
import clsx from 'clsx'
import Const from 'constants/constant'
import dayjs from 'dayjs'
import useGetAnnotationColor from 'hooks/useGetAnnotationColor'
import PreviewFile from 'pages/building/panoramas/components/PreviewFile'
import { useRecoilState } from 'recoil'
import { currentAnnotation } from 'store/krpanoStore'
import useSWR from 'swr'
import { AnnotationType } from 'types/building'
import Krpano from 'utils/krpano'

import Modal from 'components/atoms/Modal'
import CloseIcon from 'components/icons/CloseIcon'
import RightArrowIcon from 'components/icons/RightArrowIcon'

import ViewerFilePreview from '../ViewerFilePreview'

import BCPAnnotationHeader from './BCPAnnotationHeader'

import 'quill/dist/quill.snow.css'

type BCPFolder = {
  id: number
  categoryId: number
  name: string
  order: number
  createdAt: string
  updatedAt: string
  childFolders: {
    id: number
    categoryId: number
    name: string
    order: number
    createdAt: string
    updatedAt: string
    parentId: number
  }[]
}

const addHttps = (link: string): string =>
  link.startsWith('http') ? link : `https://${link}`

const truncateText = (text: string | undefined, maxLength = 35) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

export default function BCPViewerAnnotationModal() {
  const { t } = useTranslation()
  const { id } = useParams()
  const { getAnnotationBgColor } = useGetAnnotationColor()
  const [selectedTabs, setSelectedTabs] = useState<number | undefined>(
    undefined
  )
  const [previewFileIndex, setPreviewFileIndex] = useState<number>()
  const [currentSelected, setCurrentSelected] =
    useRecoilState(currentAnnotation)
  const [openedCat, setOpenedCat] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: 'name' | 'updatedAt' | 'size'
    order: 'asc' | 'desc'
  }>({ key: 'updatedAt', order: 'desc' })

  const handleSort = useCallback((key: 'name' | 'updatedAt' | 'size') => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key
      const nextOrder = isSameKey && prev.order === 'asc' ? 'desc' : 'asc'
      return { key, order: nextOrder }
    })
  }, [])

  const formatBytes = (
    bytes: number | string | undefined | null,
    decimals = 1
  ) => {
    if (!bytes) return '--'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(Number(bytes)) / Math.log(k))
    return `${parseFloat((Number(bytes) / k ** i).toFixed(dm))}${sizes[i]}`
  }

  // get custom folder category
  const { data: bcpCustomFolders } = useSWR<BCPFolder[]>({
    url: `v1/bcp/buildings/${id}/folders`
  })

  // get current header table for current annotation type
  const currentHeadertableBackgroundColor = useMemo(
    () => getAnnotationBgColor(currentSelected?.type || 0),
    [getAnnotationBgColor, currentSelected?.type]
  )

  // get current tab of file by category
  const getTabIndexOfFileByCategory = useCallback(
    (category: number): number | null => {
      // get all annotation types as number[]
      const annotationTypeValues = Object.values(AnnotationType).filter(
        (v) => typeof v === 'number'
      ) as number[]

      // if category is annotation type
      if (annotationTypeValues.includes(category - 1)) return 1 // file is tab  準備
      if (annotationTypeValues.includes(category - 2)) return 2 // file is tab  発生
      if (annotationTypeValues.includes(category - 3)) return 3 // file is tab  復旧

      // with custom folder
      return (
        bcpCustomFolders
          ?.flatMap((folder) => folder.childFolders)
          .find((child) => child.categoryId === category)?.order || null
      )
    },
    [bcpCustomFolders]
  )

  // Compute listTabs: only show tabs with files or root fields for that tab (1,2,3)
  const listTabs = useMemo((): { value: number; label: string }[] => {
    if (!currentSelected) return []
    // Get all last digits from file categories
    const fileLastDigits = Array.from(
      new Set(
        (currentSelected.files || []).map((file) =>
          typeof file.category === 'number'
            ? getTabIndexOfFileByCategory(file.category)
            : null
        )
      )
    )
    // Check which tabs have data in root fields
    const rootTabDigits: number[] = []
    if (currentSelected.link || currentSelected.links?.length)
      rootTabDigits.push(1)
    if (
      currentSelected.link_occurrence ||
      currentSelected.link_occurrences?.length
    )
      rootTabDigits.push(2)
    if (
      currentSelected.link_recovery ||
      currentSelected.link_recoveries?.length
    )
      rootTabDigits.push(3)
    if (currentSelected.memo) rootTabDigits.push(1)
    if (currentSelected.memo_occurence) rootTabDigits.push(2)
    if (currentSelected.memo_recovery) rootTabDigits.push(3)

    // Combine and dedupe
    const allDigits = Array.from(
      new Set([...fileLastDigits, ...rootTabDigits])
    ).filter((v): v is number => typeof v === 'number')
    return allDigits.sort().map((tabIndex) => ({
      value: tabIndex,
      label: Const.BCP_ANNOTATION_TABS[tabIndex - 1]?.label || `Tab ${tabIndex}`
    }))
  }, [currentSelected, getTabIndexOfFileByCategory])

  useEffect(() => {
    if (listTabs.length) setSelectedTabs(listTabs[0].value)
  }, [listTabs])

  const hasAnyDataOverall = useMemo(() => {
    if (!currentSelected) return false
    return !Krpano.IsAnnotationDataEmpty(currentSelected)
  }, [currentSelected])

  const filesOfCurrentTab = useMemo(() => {
    if (!selectedTabs || !currentSelected) return []
    return (currentSelected.files || []).filter(
      (file) =>
        typeof file.category === 'number' &&
        getTabIndexOfFileByCategory(file.category) === selectedTabs
    )
  }, [currentSelected, getTabIndexOfFileByCategory, selectedTabs])

  const sortedFilesOfCurrentTab = useMemo(() => {
    return [...filesOfCurrentTab].sort((a, b) => {
      const { key, order } = sortConfig
      let comparison = 0
      if (key === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '')
      } else if (key === 'updatedAt') {
        const dateA = dayjs(a.updatedAt || a.createdAt)
        const dateB = dayjs(b.updatedAt || b.createdAt)
        comparison = dateA.isAfter(dateB) ? 1 : -1
      } else if (key === 'size') {
        const sizeA = a.extraInfo?.size || a.size || 0
        const sizeB = b.extraInfo?.size || b.size || 0
        comparison = Number(sizeA) - Number(sizeB)
      }
      return order === 'asc' ? comparison : -comparison
    })
  }, [filesOfCurrentTab, sortConfig])

  const filesByType = useMemo(() => {
    return {
      image: sortedFilesOfCurrentTab.filter((f) =>
        ['jpg', 'jpeg', 'png'].includes((f.extension || '').toLowerCase())
      ),
      video: sortedFilesOfCurrentTab.filter((f) =>
        ['mp4', 'mov', 'MOV'].includes((f.extension || '').toLowerCase())
      ),
      pdf: sortedFilesOfCurrentTab.filter(
        (f) => (f.extension || '').toLowerCase() === 'pdf'
      )
    }
  }, [sortedFilesOfCurrentTab])

  // Always get link/memo from root annotation fields for the current tab
  const linkText = useMemo(() => {
    if (!selectedTabs || !currentSelected) return { link: '', name: '' }
    switch (selectedTabs) {
      case 2:
        return {
          link: currentSelected.link_occurrence || '',
          name: currentSelected.name_occurrence || ''
        }
      case 3:
        return {
          link: currentSelected.link_recovery || '',
          name: currentSelected.name_recovery || ''
        }
      default:
        return {
          link: currentSelected.link || '',
          name: currentSelected.name || ''
        }
    }
  }, [selectedTabs, currentSelected])

  const currentTabLinks = useMemo(() => {
    if (!selectedTabs || !currentSelected) return []

    let rawLinks = []

    switch (selectedTabs) {
      case 2:
        rawLinks = currentSelected.link_occurrences || []
        return rawLinks
          .map((l) => ({
            link: l.link_occurrence,
            name: l.name_occurrence
          }))
          .filter((item) => item?.name?.trim() || item?.link?.trim())

      case 3:
        rawLinks = currentSelected.link_recoveries || []
        return rawLinks
          .map((l) => ({
            link: l.link_recovery,
            name: l.name_recovery
          }))
          .filter((item) => item?.name?.trim() || item?.link?.trim())

      default:
        return (currentSelected.links || []).filter(
          (item) => item?.name?.trim() || item?.link?.trim()
        )
    }
  }, [selectedTabs, currentSelected])

  const memoText = useMemo(() => {
    if (!selectedTabs || !currentSelected) return ''
    switch (selectedTabs) {
      case 2:
        return currentSelected.memo_occurence || ''
      case 3:
        return currentSelected.memo_recovery || ''
      default:
        return currentSelected.memo || ''
    }
  }, [selectedTabs, currentSelected])

  const selectedCustomFolderLabel = useMemo(
    () =>
      bcpCustomFolders?.find(
        (item: any) => item.categoryId === currentSelected?.type
      )?.name || '',
    [bcpCustomFolders, currentSelected?.type]
  )

  const handleCloseModal = () => {
    setCurrentSelected(undefined)
  }

  const hasAnyData =
    filesByType.image.length > 0 ||
    filesByType.video.length > 0 ||
    filesByType.pdf.length > 0 ||
    !!linkText.link ||
    currentTabLinks.length > 0 ||
    !!memoText

  // Memo content state for async fetch
  const [fetchedMemoContent, setFetchedMemoContent] = useState('')
  const [isLoadingMemo, setIsLoadingMemo] = useState(false)

  // Fetch memo content if it's an S3 link
  useEffect(() => {
    let isMounted = true
    if (memoText) {
      setIsLoadingMemo(true)
      Krpano.FetchMemoContent(memoText).then((content) => {
        if (isMounted) {
          setFetchedMemoContent(content)
          setIsLoadingMemo(false)
        }
      })
    } else {
      setFetchedMemoContent('')
      setIsLoadingMemo(false)
    }
    return () => {
      isMounted = false
    }
  }, [memoText])

  return (
    <>
      <Modal
        width="840px"
        rootClassName="annotation-detail-modal"
        transitionName="ant-modal"
      >
        <div
          className="absolute top-5 right-5 cursor-pointer"
          onClick={handleCloseModal}
        >
          <CloseIcon />
        </div>
        {currentSelected?.isMutating ? (
          <div className="h-[560px]" />
        ) : (
          <>
            <div className="mr-20">
              <BCPAnnotationHeader
                type={currentSelected?.type}
                label={selectedCustomFolderLabel}
              />
            </div>
            {!hasAnyDataOverall ? (
              <div className="mx-10 h-[560px] flex flex-col items-center justify-center">
                <Empty description="" />
                <div className="text-xs text-center">データがありません</div>
              </div>
            ) : (
              <div className="mt-6 h-[320px] grid grid-rows-[auto_1fr]">
                {/* Tabs */}
                <div
                  className={`flex relative ${currentHeadertableBackgroundColor} h-10 mt-[10px]`}
                >
                  {listTabs.map((item, index) => (
                    <div
                      key={item.value}
                      className={`flex w-[168px] h-10 cursor-pointer ${
                        listTabs.length > 1 && index !== listTabs.length - 1
                          ? 'border-0 border-r border-solid border-white'
                          : 'border-r-0'
                      }`}
                      onClick={() => {
                        setSelectedTabs(item.value)
                        setOpenedCat(null)
                      }}
                    >
                      <div
                        className={`text-base m-auto ${
                          selectedTabs === item.value
                            ? 'text-[#247F52]'
                            : 'text-white'
                        }`}
                      >
                        {t(item.label)}
                      </div>
                    </div>
                  ))}
                  {listTabs.length > 1 ? (
                    <div
                      style={{
                        position: 'absolute',
                        left:
                          listTabs.findIndex(
                            (tab) => tab.value === selectedTabs
                          ) * 168,
                        bottom: 0
                      }}
                      className="transition-all w-[168px] h-[2px] bg-[#247F52]"
                    />
                  ) : null}
                </div>

                {/* Category blocks for current tab */}
                <div className="overflow-auto py-2">
                  {!hasAnyData && (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Empty description="" />
                      <div className="text-xs text-center">
                        データがありません
                      </div>
                    </div>
                  )}
                  {hasAnyData &&
                    openedCat === null &&
                    (() => {
                      return ['image', 'video', 'pdf', 'link', 'memo'].map(
                        (cat) => {
                          const hasData =
                            (cat === 'image' && filesByType.image.length > 0) ||
                            (cat === 'video' && filesByType.video.length > 0) ||
                            (cat === 'pdf' && filesByType.pdf.length > 0) ||
                            (cat === 'link' &&
                              (linkText.link || currentTabLinks.length > 0)) ||
                            (cat === 'memo' && memoText)

                          if (!hasData) return null

                          const categoriesWithData = [
                            'image',
                            'video',
                            'pdf',
                            'link',
                            'memo'
                          ].filter(
                            (c) =>
                              (c === 'image' && filesByType.image.length > 0) ||
                              (c === 'video' && filesByType.video.length > 0) ||
                              (c === 'pdf' && filesByType.pdf.length > 0) ||
                              (c === 'link' &&
                                (linkText.link ||
                                  currentTabLinks.length > 0)) ||
                              (c === 'memo' && memoText)
                          )
                          const isLast =
                            categoriesWithData[
                              categoriesWithData.length - 1
                            ] === cat

                          return (
                            <div
                              key={cat}
                              className={
                                !isLast || categoriesWithData.length === 1
                                  ? 'border-0 border-b border-solid border-[#d9d9d9] p-2'
                                  : 'p-2'
                              }
                            >
                              <div
                                className={`flex items-center px-2 py-1 font-medium bg-white ${
                                  !isLast ? 'border-b border-gray-200' : ''
                                } cursor-pointer`}
                                onClick={() => setOpenedCat(cat)}
                              >
                                <span>
                                  {cat === 'image' && t('translation.image')}
                                  {cat === 'video' && t('translation.video')}
                                  {cat === 'pdf' && t('translation.pdf')}
                                  {cat === 'link' && t('translation.link')}
                                  {cat === 'memo' && t('translation.handover')}
                                </span>
                                <span
                                  className="ml-auto transition-transform duration-200"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    transform: 'rotate(0deg)'
                                  }}
                                >
                                  <RightArrowIcon color="#021120" />
                                </span>
                              </div>
                            </div>
                          )
                        }
                      )
                    })()}
                  {hasAnyData && openedCat !== null && (
                    <div className="p-2">
                      {/* Breadcrumb */}
                      <div className="flex items-center mb-4 gap-x-2">
                        <span
                          className="text-[#247F52] font-bold cursor-pointer hover:opacity-70 duration-150"
                          onClick={() => setOpenedCat(null)}
                        >
                          {t('translation.toppu')}
                        </span>
                        <RightArrowIcon color="black" size={14} />
                        <span className="font-semibold">
                          {openedCat === 'image' && t('translation.image')}
                          {openedCat === 'video' && t('translation.video')}
                          {openedCat === 'pdf' && t('translation.pdf')}
                          {openedCat === 'link' && t('translation.link')}
                          {openedCat === 'memo' && t('translation.handover')}
                        </span>
                      </div>
                      {/* Detail view for cat */}
                      <div className="px-2 bg-white">
                        {openedCat === 'image' && (
                          <>
                            <div
                              className="grid items-center px-1 py-1 border-0 border-b border-solid border-[#EEEFF2] mb-3"
                              style={{
                                gridTemplateColumns: `120px 1fr 120px 100px`,
                                gap: '12px'
                              }}
                            >
                              <div />
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('name')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'name',
                                    'text-[#A0A0A0]': sortConfig.key !== 'name'
                                  })}
                                >
                                  名前
                                  {sortConfig.key === 'name' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('updatedAt')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'updatedAt',
                                    'text-[#A0A0A0]':
                                      sortConfig.key !== 'updatedAt'
                                  })}
                                >
                                  更新日
                                  {sortConfig.key === 'updatedAt' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div className="flex items-center gap-1">
                                <Typography.Text className="font-bold text-[13px] text-[#A0A0A0]">
                                  サイズ
                                </Typography.Text>
                              </div>
                            </div>
                            {filesByType.image.map((file) => {
                              const fileIndex =
                                sortedFilesOfCurrentTab.findIndex(
                                  (f) => f.id === file.id
                                )
                              return (
                                <div
                                  key={file.id}
                                  className={`grid items-center gap-[12px] cursor-pointer mb-2 transition-colors hover:bg-gray-50 p-1 rounded ${
                                    fileIndex !==
                                    sortedFilesOfCurrentTab.length - 1
                                      ? 'border-b border-gray-100'
                                      : ''
                                  }`}
                                  style={{
                                    gridTemplateColumns: `120px 1fr 120px 100px`
                                  }}
                                  onClick={() => setPreviewFileIndex(fileIndex)}
                                >
                                  <PreviewFile file={file} size="large" />
                                  <Typography.Text
                                    ellipsis={{ tooltip: file.name }}
                                    className="text-sm"
                                  >
                                    {file.name}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {dayjs(
                                      file.updatedAt || file.createdAt
                                    ).format('YYYY/MM/DD')}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {formatBytes(
                                      file.extraInfo?.size || file.size
                                    )}
                                  </Typography.Text>
                                </div>
                              )
                            })}
                          </>
                        )}
                        {openedCat === 'video' && (
                          <>
                            <div
                              className="grid items-center px-1 py-1 border-0 border-b border-solid border-[#EEEFF2] mb-3"
                              style={{
                                gridTemplateColumns: `120px 1fr 120px 100px`,
                                gap: '12px'
                              }}
                            >
                              <div />
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('name')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'name',
                                    'text-[#A0A0A0]': sortConfig.key !== 'name'
                                  })}
                                >
                                  名前
                                  {sortConfig.key === 'name' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('updatedAt')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'updatedAt',
                                    'text-[#A0A0A0]':
                                      sortConfig.key !== 'updatedAt'
                                  })}
                                >
                                  更新日
                                  {sortConfig.key === 'updatedAt' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div className="flex items-center gap-1">
                                <Typography.Text className="font-bold text-[13px] text-[#A0A0A0]">
                                  サイズ
                                </Typography.Text>
                              </div>
                            </div>
                            {filesByType.video.map((file) => {
                              const fileIndex =
                                sortedFilesOfCurrentTab.findIndex(
                                  (f) => f.id === file.id
                                )
                              return (
                                <div
                                  key={file.id}
                                  className={`grid items-center gap-[12px] cursor-pointer mb-2 transition-colors hover:bg-gray-50 p-1 rounded ${
                                    fileIndex !==
                                    sortedFilesOfCurrentTab.length - 1
                                      ? 'border-b border-gray-100'
                                      : ''
                                  }`}
                                  style={{
                                    gridTemplateColumns: `120px 1fr 120px 100px`
                                  }}
                                  onClick={() => setPreviewFileIndex(fileIndex)}
                                >
                                  <PreviewFile file={file} size="large" />
                                  <Typography.Text
                                    ellipsis={{ tooltip: file.name }}
                                    className="text-sm"
                                  >
                                    {file.name}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {dayjs(
                                      file.updatedAt || file.createdAt
                                    ).format('YYYY/MM/DD')}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {formatBytes(
                                      file.extraInfo?.size || file.size
                                    )}
                                  </Typography.Text>
                                </div>
                              )
                            })}
                          </>
                        )}
                        {openedCat === 'pdf' && (
                          <>
                            <div
                              className="grid items-center px-1 py-1 border-0 border-b border-solid border-[#EEEFF2] mb-3"
                              style={{
                                gridTemplateColumns: `120px 1fr 120px 100px`,
                                gap: '12px'
                              }}
                            >
                              <div />
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('name')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'name',
                                    'text-[#A0A0A0]': sortConfig.key !== 'name'
                                  })}
                                >
                                  名前
                                  {sortConfig.key === 'name' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div
                                className="flex items-center gap-1 cursor-pointer select-none"
                                onClick={() => handleSort('updatedAt')}
                              >
                                <Typography.Text
                                  className={clsx('font-bold text-[13px]', {
                                    'theme-text-primary':
                                      sortConfig.key === 'updatedAt',
                                    'text-[#A0A0A0]':
                                      sortConfig.key !== 'updatedAt'
                                  })}
                                >
                                  更新日
                                  {sortConfig.key === 'updatedAt' &&
                                    (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
                                </Typography.Text>
                              </div>
                              <div className="flex items-center gap-1">
                                <Typography.Text className="font-bold text-[13px] text-[#A0A0A0]">
                                  サイズ
                                </Typography.Text>
                              </div>
                            </div>
                            {filesByType.pdf.map((file) => {
                              const fileIndex =
                                sortedFilesOfCurrentTab.findIndex(
                                  (f) => f.id === file.id
                                )
                              return (
                                <div
                                  key={file.id}
                                  className={`grid items-center gap-[12px] cursor-pointer mb-2 transition-colors hover:bg-gray-50 p-1 rounded ${
                                    fileIndex !==
                                    sortedFilesOfCurrentTab.length - 1
                                      ? 'border-b border-gray-100'
                                      : ''
                                  }`}
                                  style={{
                                    gridTemplateColumns: `120px 1fr 120px 100px`
                                  }}
                                  onClick={() => setPreviewFileIndex(fileIndex)}
                                >
                                  <PreviewFile file={file} size="large" />
                                  <Typography.Text
                                    ellipsis={{ tooltip: file.name }}
                                    className="text-sm"
                                  >
                                    {file.name}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {dayjs(
                                      file.updatedAt || file.createdAt
                                    ).format('YYYY/MM/DD')}
                                  </Typography.Text>
                                  <Typography.Text className="text-[13px] whitespace-nowrap">
                                    {formatBytes(
                                      file.extraInfo?.size || file.size
                                    )}
                                  </Typography.Text>
                                </div>
                              )
                            })}
                          </>
                        )}
                        {openedCat === 'link' && (
                          <div className="flex flex-col gap-12 w-full items-center">
                            {currentTabLinks.length > 0
                              ? currentTabLinks.map((linkItem, idx) => {
                                  const name = linkItem?.name?.trim()
                                  const link = linkItem?.link?.trim()
                                  const text = name || link
                                  const hasLink = !!link

                                  return (
                                    <div
                                      key={`link-${link || idx}`}
                                      className="text-center max-w-full"
                                    >
                                      <Tooltip title={text}>
                                        {hasLink ? (
                                          <a
                                            href={addHttps(link)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#007AFF] underline block"
                                          >
                                            <span>{truncateText(text)}</span>
                                          </a>
                                        ) : (
                                          <span className="block text-black">
                                            {truncateText(text)}
                                          </span>
                                        )}
                                      </Tooltip>
                                    </div>
                                  )
                                })
                              : linkText.name && (
                                  <div className="max-w-full overflow-hidden text-center">
                                    <Tooltip
                                      title={linkText.name || linkText.link}
                                    >
                                      {linkText.link?.trim() ? (
                                        <a
                                          href={addHttps(linkText.link)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-[#007AFF] underline block"
                                        >
                                          <span>
                                            {truncateText(
                                              linkText.name || linkText.link
                                            )}
                                          </span>
                                        </a>
                                      ) : (
                                        <span className="block text-black">
                                          {truncateText(linkText.name)}
                                        </span>
                                      )}
                                    </Tooltip>
                                  </div>
                                )}
                          </div>
                        )}
                        {openedCat === 'memo' && (
                          <div className="ql-container ql-snow h-[150px] mt-3">
                            <div className="ql-editor">
                              {isLoadingMemo ? (
                                <span>読み込み中...</span>
                              ) : (
                                fetchedMemoContent &&
                                (/^(https?:\/\/)/.test(
                                  fetchedMemoContent.trim()
                                ) ? (
                                  <a
                                    href={fetchedMemoContent.trim()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#007AFF] underline hover:underline"
                                  >
                                    {fetchedMemoContent.trim()}
                                  </a>
                                ) : (
                                  <span
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                      __html: fetchedMemoContent
                                    }}
                                  />
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={sortedFilesOfCurrentTab[previewFileIndex]}
          maxLength={sortedFilesOfCurrentTab.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
    </>
  )
}
